import { useDeferredValue, useState } from 'react';

import { Checkbox } from '@flintmint/web-shared/components/checkbox';

import { usePageTitle } from '@/hooks/use-page-title';
import { useFlows } from '@/hooks/use-flows';
import { useMedicalModules } from '@/hooks/use-medical-modules';
import { useModuleSearch } from '@/hooks/use-module-search';
import { useSelection } from '@/hooks/use-selection';
import type { FlowWithId } from '@/services/flows-service';
import type { MedicalModuleWithId } from '@/services/medical-modules-service';
import { useModuleIdsByName } from './module-name';
import { BulkActionBar } from './components/bulk-action-bar/bulk-action-bar';
import { ModuleSection } from './components/module-section/module-section';

import type { ModuleType } from '@flintmint/core-shared/firestore/schemas';

import styles from './medical-module-dashboard.module.scss';

const SECTIONS: { types: ModuleType[]; label: string }[] = [
  { types: ['complaint'], label: 'Chief Complaint Modules' },
  { types: ['procedure', 'other'], label: 'Other Modules' },
];

const ALL_TYPES = SECTIONS.flatMap(({ types }) => types);

export const MedicalModuleDashboard = () => {
  usePageTitle('Flow Modules');
  const { flows, error: flowsError } = useFlows();
  const { modules, isLoading, error: modulesError } = useMedicalModules(ALL_TYPES);
  const moduleIdsByName = useModuleIdsByName(modules);
  const [query, setQuery] = useState('');
  const [flowId, setFlowId] = useState('all');
  const [enabled, setEnabled] = useState('all');

  // The input stays responsive while the (potentially long) row list catches up.
  const deferredQuery = useDeferredValue(query);

  const searchedIds = useModuleSearch(modules, flows, deferredQuery);
  const searchedModules = searchedIds ? inOrder(modules, searchedIds) : modules;

  const filters: ModuleFilters = { flowId, enabled };
  const matchingModules = searchedModules.filter((module) => matchesFilters(module, filters));

  // Selected ids outlive filter changes, as in the web-app library; modules deleted meanwhile drop out here.
  const { selectedIds, toggle, toggleMany, deselect, clear } = useSelection();
  const selectedModules = modules.filter((module) => selectedIds.has(module.id));
  const selectedMatchingCount = matchingModules.filter((module) => selectedIds.has(module.id)).length;

  return (
    <>
      <div className={styles.dashboard} data-has-selection={selectedModules.length > 0 || undefined}>
        <ModuleFilterBar
          query={query}
          onQueryChange={setQuery}
          flows={flows}
          flowId={flowId}
          onFlowIdChange={setFlowId}
          enabled={enabled}
          onEnabledChange={setEnabled}
          matchingCount={matchingModules.length}
          selectedMatchingCount={selectedMatchingCount}
          onToggleSelectAll={() => toggleMany(matchingModules.map((module) => module.id))}
        />
        {flowsError && <p className={styles.error}>{flowsError}</p>}
        {modulesError && <p className={styles.error}>{modulesError}</p>}
        {/* A failed first load has no rows; empty sections would read as "there are no modules". */}
        {!(modulesError && modules.length === 0) &&
          SECTIONS.map(({ types, label }) => (
            <ModuleSection
              key={types.join(',')}
              types={types}
              label={label}
              modules={ofTypes(matchingModules, types)}
              moduleIdsByName={moduleIdsByName}
              totalCount={ofTypes(modules, types).length}
              flows={flows}
              isLoading={isLoading}
              selectedIds={selectedIds}
              onToggleSelect={toggle}
              onToggleSelectMany={toggleMany}
            />
          ))}
        <BulkActionBar modules={selectedModules} flows={flows} onClear={clear} onDeselect={deselect} />
      </div>
    </>
  );
};

// The dashboard as it looks while the modules load, its controls held still until the dashboard takes over.
export const MedicalModuleDashboardSkeleton = () => (
  <div className={styles.dashboard}>
    <ModuleFilterBar
      query=""
      onQueryChange={noop}
      flows={[]}
      flowId="all"
      onFlowIdChange={noop}
      enabled="all"
      onEnabledChange={noop}
      matchingCount={0}
      selectedMatchingCount={0}
      onToggleSelectAll={noop}
      disabled
    />
    {SECTIONS.map(({ types, label }) => (
      <ModuleSection
        key={types.join(',')}
        types={types}
        label={label}
        modules={[]}
        moduleIdsByName={new Map()}
        totalCount={0}
        flows={[]}
        isLoading
        selectedIds={new Set()}
        onToggleSelect={noop}
        onToggleSelectMany={noop}
      />
    ))}
  </div>
);

const noop = () => {};

const ModuleFilterBar = ({
  query,
  onQueryChange,
  flows,
  flowId,
  onFlowIdChange,
  enabled,
  onEnabledChange,
  matchingCount,
  selectedMatchingCount,
  onToggleSelectAll,
  disabled = false,
}: {
  query: string;
  onQueryChange: (query: string) => void;
  flows: FlowWithId[];
  flowId: string;
  onFlowIdChange: (flowId: string) => void;
  enabled: string;
  onEnabledChange: (enabled: string) => void;
  matchingCount: number;
  selectedMatchingCount: number;
  onToggleSelectAll: () => void;
  disabled?: boolean;
}) => (
  <div className={styles.filterBar}>
    <input
      className={styles.searchInput}
      value={query}
      onChange={(e) => onQueryChange(e.target.value)}
      disabled={disabled}
      placeholder="Search name, criteria, content, flows…"
      data-testid="module-search-input"
    />
    <select
      className={styles.filterSelect}
      value={flowId}
      onChange={(e) => onFlowIdChange(e.target.value)}
      disabled={disabled}
      data-testid="module-flow-filter"
    >
      <option value="all">All flows</option>
      <option value="none">No flow</option>
      {flows.map((flow) => (
        <option key={flow.id} value={flow.id}>
          {flow.name}
        </option>
      ))}
    </select>
    <select
      className={styles.filterSelect}
      value={enabled}
      onChange={(e) => onEnabledChange(e.target.value)}
      disabled={disabled}
      data-testid="module-enabled-filter"
    >
      <option value="all">Enabled + disabled</option>
      <option value="enabled">Enabled only</option>
      <option value="disabled">Disabled only</option>
    </select>
    <Checkbox
      className={styles.selectAll}
      checked={matchingCount > 0 && selectedMatchingCount === matchingCount}
      intermediate={selectedMatchingCount > 0 && selectedMatchingCount < matchingCount}
      disabled={matchingCount === 0}
      onChange={onToggleSelectAll}
      label={`Select all ${matchingCount} matching`}
      data-testid="module-select-all-matching"
    />
  </div>
);

const inOrder = (modules: MedicalModuleWithId[], ids: string[]) => {
  const modulesById = new Map(modules.map((module) => [module.id, module]));
  return ids.flatMap((id) => modulesById.get(id) ?? []);
};

type ModuleFilters = {
  flowId: string;
  enabled: string;
};

const matchesFilters = (module: MedicalModuleWithId, filters: ModuleFilters): boolean => {
  const flowIds = module.flowIds ?? [];
  const matchesFlow =
    filters.flowId === 'all' || (filters.flowId === 'none' ? flowIds.length === 0 : flowIds.includes(filters.flowId));

  // A module without the field counts as enabled, as in the row switch and the backend loader.
  const isEnabled = module.enabled !== false;
  const matchesEnabled = filters.enabled === 'all' || (filters.enabled === 'enabled') === isEnabled;

  return matchesFlow && matchesEnabled;
};

const ofTypes = (modules: MedicalModuleWithId[], types: ModuleType[]) => modules.filter((module) => types.includes(module.type));
